import React from "react";
import { SlidersHorizontal } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { Switch } from "@/components/ui/Switch";
import DateRangeFilter from "@/components/shared/DateRangeFilter";
import UsageChip from "./UsageChip";

/**
 * The one control bar every usage screen wears.
 *
 * It replaces two stacked cards — a date-range card and a full-width checkbox
 * card — that between them took a third of the first screen before a single
 * number appeared. One row now carries the scope, the window and the depth,
 * which is the whole set of questions a reader can ask of these pages.
 *
 * **Explicit dates, with presets as a shortcut.** The API takes literal bounds,
 * so nothing here has to decide whose month applies. The dates come from the
 * app's own `DateRangeFilter`, the same control the audit log wears, rather
 * than the browser's native date input — which drew its own calendar, in its
 * own locale's order, and looked like nothing else on screen. That control also
 * settles what a picked day means: the reader's own day, on this screen and on
 * the audit log alike. See `windowParams` in `useTenantUsage.js`.
 * The preset chips resolve to the same two dates a person could pick, and the
 * active chip is derived from the dates rather than stored — so a range picked
 * by hand that happens to be the last 30 days lights the chip, and every path
 * to the same window looks the same.
 *
 * **Sticky is the bar's own job.** A page that passes `sticky` gets the bar in a
 * band that pins to the top of the scrolling area. The band is opaque, and it
 * paints over the layout's own top padding as well: the scroll container pins a
 * sticky child at its *padding* edge, so without that cover a strip of the page
 * kept scrolling in full view above the bar.
 *
 * @param {{from: string, to: string}} range   yyyy-mm-dd each, "" for unbounded
 * @param {React.ReactNode} leading            scope control, e.g. the tenant picker
 * @param {boolean|undefined} detail           engineering switch; omit to hide it
 * @param {boolean} sticky                     pin the bar while the page scrolls
 */
const PRESETS = [
  { label: "7d", title: "Last 7 days", days: 7 },
  { label: "30d", title: "Last 30 days", days: 30 },
  { label: "90d", title: "Last 90 days", days: 90 },
];

/**
 * A Date as the `yyyy-mm-dd` the picker and the range state speak.
 *
 * Built from the **local** calendar fields, not `toISOString()`. The picker
 * names local days, so a UTC-derived string disagreed with it wherever the
 * offset crosses midnight: east of Greenwich early in the morning the `7d`
 * chip resolved to yesterday's date, so picking the same window by hand never
 * lit the chip, and "last 7 days" quietly meant the 7 days ending yesterday.
 */
const toInputDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const presetRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { from: toInputDate(start), to: toInputDate(end) };
};

/** Which chip, if any, the current dates already describe. */
const activePreset = (range) => {
  if (!range.from && !range.to) return "all";
  const match = PRESETS.find((preset) => {
    const candidate = presetRange(preset.days);
    return candidate.from === range.from && candidate.to === range.to;
  });
  return match?.label ?? null;
};

const UsageToolbar = ({ range, onRangeChange, disabled, leading, detail, onDetailChange, sticky = false }) => {
  const active = activePreset(range);
  const showDetail = detail !== undefined && typeof onDetailChange === "function";

  const bar = (
    <Card className="flex flex-col gap-3 p-3 lg:flex-row lg:items-center lg:gap-4">
      {leading ? <div className="min-w-0 lg:w-72 lg:flex-shrink-0">{leading}</div> : null}

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
        {/* No calendar glyph here — `DateRangeFilter` beside it carries one,
            and two of the same icon a few pixels apart read as a slip. */}
        <div className="flex items-center gap-1 rounded-lg border border-border-primary bg-background-primary p-0.5">
          {PRESETS.map((preset) => (
            <UsageChip
              key={preset.label}
              title={preset.title}
              active={active === preset.label}
              disabled={disabled}
              onClick={() => onRangeChange(presetRange(preset.days))}
            >
              {preset.label}
            </UsageChip>
          ))}
          <UsageChip title="Every record ever written" active={active === "all"} disabled={disabled} onClick={() => onRangeChange({ from: "", to: "" })}>
            All time
          </UsageChip>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter name="usage" from={range.from} to={range.to} onChange={onRangeChange} disabled={disabled} />
          {/* Said "UTC" while the window was built from UTC days. It is the
              reader's own day now, on this screen and on the audit log both. */}
          <span className="text-[11px] text-text-secondary" title="A date covers that whole day where you are.">
            your time
          </span>
        </div>
      </div>

      {/* The control is the app's own `Switch`, not a track and thumb written
          out again here. The tone on the surrounding panel still marks the on
          state, which is what a reader sees from across the page. */}
      {showDetail && (
        <div
          title="Model call counts, cache hits, failures and the per-feature breakdown."
          className={`flex flex-shrink-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${disabled ? "opacity-60" : ""} ${
            detail ? "border-tone-violet/40 bg-tone-violet-soft text-tone-violet" : "border-border-primary bg-background-primary text-text-secondary"
          }`}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <label className="cursor-pointer whitespace-nowrap" htmlFor="usage-detail">
            Engineering detail
          </label>
          <Switch id="usage-detail" checked={!!detail} disabled={disabled} onToggle={() => onDetailChange(!detail)} label="engineering detail" />
        </div>
      )}
    </Card>
  );

  if (!sticky) return bar;

  // `-mx-4 px-4` spans the layout's side padding, and the band's own 4px top
  // padding plus the 12px `before` block cover the 16px of top padding above
  // the pin point. Both are opaque: a translucent band let the scrolled cards
  // read through the bar.
  //
  // 12px, not 16px, is exactly the `gap-3` the layout leaves between the header
  // and the first card. A 16px block reached 4px further and painted over the
  // bottom of the header — the notification bell is the tallest thing in that
  // row, so it was the one that looked cut off.
  return (
    <div className="sticky top-0 z-10 -mx-4 bg-background-primary px-4 py-1 before:absolute before:inset-x-0 before:bottom-full before:h-3 before:bg-background-primary before:content-['']">
      {bar}
    </div>
  );
};

export default UsageToolbar;
